import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { getZoomAPI } from '../utils/zoomAPI'
import { generateCodeVerifier, generateCodeChallenge, generateState } from '../utils/pkce'
import type { User } from '@supabase/supabase-js'

export type AuthState = 'loading' | 'signed-out' | 'signed-in'

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const zoomAPI = getZoomAPI()

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      // First, check if there's a session_token in URL (from Google OAuth callback)
      const urlParams = new URLSearchParams(window.location.search)
      const sessionToken = urlParams.get('session_token')

      if (sessionToken) {
        console.log('[useAuth] Session token found in URL, exchanging...')

        try {
          // Exchange one-time token for full session
          const { data, error } = await supabase.functions.invoke('exchange-session-token', {
            body: { token: sessionToken }
          })

          if (error) throw error

          // Set session from tokens
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: data.access_token,
            refresh_token: data.refresh_token
          })

          if (sessionError || !session) throw sessionError || new Error('No session returned')

          console.log('[useAuth] ✅ Session set from token:', session.user.email)
          setUser(session.user)
          setAuthState('signed-in')

          // Clean up URL
          window.history.replaceState(null, '', window.location.pathname)
          return
        } catch (error) {
          console.error('[useAuth] Failed to exchange session token:', error)
          // Continue to check for existing session
        }
      }

      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        console.log('[useAuth] Existing session found:', session.user.email)
        setUser(session.user)
        setAuthState('signed-in')
      } else {
        console.log('[useAuth] No existing session')
        setAuthState('signed-out')
      }
    }

    checkSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[useAuth] Auth state changed:', event)

      if (event === 'SIGNED_IN' && session) {
        setUser(session.user)
        setAuthState('signed-in')
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setAuthState('signed-out')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithEmail = async (email: string) => {
    console.log('[useAuth] Sending magic link to:', email)

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin
      }
    })

    if (error) {
      console.error('[useAuth] Failed to send magic link:', error)
      throw error
    }

    console.log('[useAuth] ✅ Magic link sent to', email)
  }

  const signInWithZoom = async () => {
    console.log('[useAuth] ========== STARTING ZOOM OAUTH ==========')
    setIsSigningIn(true)

    try {
      // Wait for Zoom SDK to initialize
      if (!zoomAPI.isReady()) {
        console.log('[useAuth] Waiting for Zoom SDK to initialize...')
        await zoomAPI.initialize()
        console.log('[useAuth] ✅ Zoom SDK initialized')
      }

      // Generate PKCE parameters
      // NOTE: Using PLAIN PKCE method (not S256) because Zoom Apps In-Client OAuth
      // doesn't support S256 despite documentation saying otherwise
      console.log('[useAuth] Generating PKCE parameters...')
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = generateCodeChallenge(codeVerifier) // PLAIN method - no await needed
      const state = generateState()
      console.log('[useAuth] ✅ PKCE parameters generated (PLAIN method)')

      // Trigger Zoom OAuth flow
      console.log('[useAuth] Triggering Zoom authorization...')
      const { code, redirectUri } = await zoomAPI.authorizeWithZoom(codeChallenge, state)
      console.log('[useAuth] ✅ Authorization code received from Zoom')
      console.log('[useAuth] Redirect URI:', redirectUri)

      // Exchange authorization code for Supabase session
      // Note: We send the exact redirectUri that Zoom returned - this MUST match
      // what Zoom expects during token exchange, otherwise we get "Invalid Grant"
      console.log('[useAuth] Calling zoom-oauth-exchange Edge Function...')
      const { data, error } = await supabase.functions.invoke('zoom-oauth-exchange', {
        body: {
          code,
          codeVerifier,
          redirectUri
        }
      })

      if (error) {
        console.error('[useAuth] ❌ Token exchange failed:', error)
        alert('Failed to exchange authorization code. Error: ' + JSON.stringify(error))
        throw error
      }

      console.log('[useAuth] ✅ Token exchange successful')

      // Set session from tokens
      console.log('[useAuth] Setting Supabase session...')
      const { data: { session }, error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token
      })

      if (sessionError || !session) {
        console.error('[useAuth] ❌ Failed to set session:', sessionError)
        alert('Failed to create session. Error: ' + (sessionError?.message || 'Unknown error'))
        throw sessionError || new Error('No session returned')
      }

      console.log('[useAuth] ✅ Zoom sign-in successful!', session.user.email)
      setUser(session.user)
      setAuthState('signed-in')

      alert('✅ Successfully signed in with Zoom!')
    } catch (error) {
      console.error('[useAuth] ❌ Zoom sign-in failed:', error)
      alert('Zoom sign-in failed. Check console for details.\nError: ' + (error instanceof Error ? error.message : String(error)))
      throw error
    } finally {
      setIsSigningIn(false)
      console.log('[useAuth] ========== ZOOM OAUTH COMPLETE ==========')
    }
  }

  const signInWithGoogle = async () => {
    console.log('[useAuth] Starting Google OAuth flow...')
    setIsSigningIn(true)

    try {
      // Wait for Zoom SDK to initialize
      if (!zoomAPI.isReady()) {
        console.log('[useAuth] Waiting for Zoom SDK to initialize...')
        await zoomAPI.initialize()
      }

      // Get Supabase project URL
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (!supabaseUrl) {
        throw new Error('Missing VITE_SUPABASE_URL environment variable')
      }

      // Build OAuth initiation URL
      const initiateUrl = `${supabaseUrl}/functions/v1/google-oauth-initiate`

      console.log('[useAuth] Opening Google OAuth in external browser...')

      // Open Google OAuth in external browser
      await zoomAPI.openUrl(initiateUrl)

      console.log('[useAuth] ✅ OAuth window opened')
      console.log('[useAuth] Please complete sign-in in the browser window')
      console.log('[useAuth] The app will automatically sign you in when you return')

      // The flow continues when user returns with session_token in URL
      // The useEffect above will detect the token and exchange it for a session
    } catch (error) {
      console.error('[useAuth] Google sign-in failed:', error)
      setIsSigningIn(false)
      throw error
    }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  return {
    authState,
    user,
    isSigningIn,
    signInWithEmail,
    signInWithZoom,
    signInWithGoogle,
    signOut
  }
}
